/**
 * Headline number with a label, an icon and a supporting line.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import { UTTooltip } from '@group-link-one/gl-ui/utilities';
import { useId } from 'react';
import type { JSX } from 'react';

export type MetricTone = 'primary' | 'success' | 'danger' | 'water';

const TONE_COLOR: Record<MetricTone, { color: string; background: string }> = {
  primary: { color: 'var(--color-primary)', background: 'var(--color-primary-subtle)' },
  success: { color: 'var(--color-success)', background: 'var(--color-success-subtle)' },
  danger: { color: 'var(--color-danger)', background: 'var(--color-danger-subtle)' },
  water: { color: 'var(--color-water)', background: 'var(--color-info-subtle)' },
};

const cardStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  min-width: 0;
`;

const headerStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
`;

/** A real button, so the tooltip is reachable by Tab — `GLTooltip` mirrors hover onto focus. */
const hintButtonStyle = css`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-text-muted);
  cursor: help;
  vertical-align: middle;

  &:hover,
  &:focus-visible {
    color: var(--color-primary);
  }
`;

/*
 * White on purpose, and not `--color-text-inverse`.
 *
 * `GLTooltip` paints its own text white, but only for the `text` prop — hand it `content` and the
 * JSX inherits the page's dark text, which is what happened here: dark on the tooltip's dark grey.
 * Its desktop background is a fixed `ULTRA_DARK_GREY` that does not follow the theme, so the
 * inverse token would flip to dark in dark mode and put us right back where we started.
 */
const hintBoxStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  text-align: left;
  font-size: var(--font-size-2xs);
  line-height: 1.45;
  text-wrap: pretty;
  color: var(--gl-white);
`;

/**
 * Read by a screen reader whether or not the floating box is on screen.
 *
 * It sits inside the label, which is uppercased for the eye — and a screen reader handed a whole
 * paragraph in capitals may spell it out letter by letter, so the label's typography is undone here.
 */
const srOnlyStyle = css`
  text-transform: none;
  letter-spacing: normal;
  font-weight: var(--font-weight-regular);
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const labelStyle = css`
  flex: 1;
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-secondary);
  min-width: 0;
`;

const iconBoxStyle = css`
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const valueStyle = css`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
`;

const footnoteStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface MetricCardProps {
  label: string;
  /** The headline number. Pass "—" when the figure is genuinely unknown. */
  value: string | number;
  icon: IGLIcon['variant'];
  tone?: MetricTone;
  footnote?: string;
  /**
   * Why the number is what it is, one line per reason. A tooltip is where an explanation that is
   * too long for the footnote goes — and the same lines become the button's description, so what a
   * screen reader hears is never a shorter story than what a mouse reveals.
   */
  hint?: string[];
  id?: string;
}

export const MetricCard = ({
  label,
  value,
  icon,
  tone = 'primary',
  footnote,
  hint,
  id,
}: MetricCardProps): JSX.Element => {
  const { color, background } = TONE_COLOR[tone];
  const hintId = useId();
  const hasHint = hint !== undefined && hint.length > 0;

  return (
    <article id={id} className={cardStyle}>
      <div className={headerStyle}>
        <span className={labelStyle}>
          {label}
          {hasHint ? (
            <>
              {' '}
              <UTTooltip
                /* Above the icon: below it, the box lands on top of the very number it is
                   explaining. */
                position='top-right'
                width='320px'
                content={
                  <span className={hintBoxStyle}>
                    {hint.map(line => (
                      <span key={line}>{line}</span>
                    ))}
                  </span>
                }
              >
                <button
                  type='button'
                  className={hintButtonStyle}
                  id={id ? `${id}-hint` : undefined}
                  aria-label={`Como ${label} é calculado`}
                  aria-describedby={hintId}
                >
                  <GLIcon variant='Communication.Info' size={14} fill='currentColor' />
                </button>
              </UTTooltip>
              <span id={hintId} className={srOnlyStyle}>
                {hint.join(' ')}
              </span>
            </>
          ) : null}
        </span>
        <span className={iconBoxStyle} style={{ background }}>
          <GLIcon variant={icon} size={18} fill={color} />
        </span>
      </div>
      <span className={valueStyle}>{value}</span>
      {footnote ? <span className={footnoteStyle}>{footnote}</span> : null}
    </article>
  );
};
